import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus } from "@phosphor-icons/react";
import { toast } from "sonner";
import { EmployeeDetail } from "@/components/EmployeeDetail";

const empty = { name: "", gender: "M", opening_balance: 0 };

export default function Employees() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [detailId, setDetailId] = useState(null);

  const load = () => api.get("/employees").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const openAdd = () => { setForm(empty); setOpen(true); };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    const payload = { ...form, opening_balance: Number(form.opening_balance) || 0 };
    try {
      await api.post("/employees", payload);
      toast.success("Labourer added");
      setOpen(false); load();
    } catch { toast.error("Save failed"); }
  };

  return (
    <div className="p-6 md:p-8 max-w-[700px] mx-auto w-full space-y-6">
      <PageHeader title="Labourers" subtitle={`${list.length} active labourer${list.length === 1 ? "" : "s"}`}>
        <Button data-testid="btn-add-labourer" onClick={openAdd} className="bg-slate-900 hover:bg-slate-800 gap-2">
          <Plus size={16} weight="bold" /> Add Labourer
        </Button>
      </PageHeader>

      <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
        <table className="w-full text-sm" data-testid="labourers-table">
          <thead>
            <tr className="bg-slate-100 text-xs uppercase tracking-widest text-slate-500">
              <th className="text-left px-4 py-3 font-semibold">Name</th>
              <th className="text-left px-4 py-3 font-semibold">Gender</th>
            </tr>
          </thead>
          <tbody>
            {list.map((e) => (
              <tr key={e.id} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="px-4 py-3">
                  <button data-testid={`open-employee-${e.id}`} onClick={() => setDetailId(e.id)}
                    className="font-medium text-slate-800 hover:text-slate-900 hover:underline text-left">
                    {e.name}
                  </button>
                  {e.recent_days_worked > 0 && (
                    <span className="ml-2 text-[10px] font-normal text-slate-400" title="Days worked in the last 30 days">
                      {e.recent_days_worked}d/30
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-slate-600">{e.gender === "M" ? "Male" : "Female"}</td>
              </tr>
            ))}
            {list.length === 0 && (
              <tr><td colSpan={2} className="px-4 py-10 text-center text-slate-500">No labourers yet. Click "Add Labourer".</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle>Add Labourer</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Name</Label>
              <Input data-testid="input-name" value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Ramesh" />
            </div>
            <div className="space-y-1.5">
              <Label>Gender</Label>
              <Select value={form.gender} onValueChange={(v) => setForm({ ...form, gender: v })}>
                <SelectTrigger data-testid="select-gender"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="M">Male</SelectItem>
                  <SelectItem value="F">Female</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Opening Balance (₹)</Label>
              <Input data-testid="input-opening" type="number" value={form.opening_balance}
                onChange={(e) => setForm({ ...form, opening_balance: e.target.value })} />
              <p className="text-xs text-slate-500">Use a negative value if the labourer owes you money.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="btn-save-labourer" className="bg-slate-900 hover:bg-slate-800" onClick={save}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <EmployeeDetail empId={detailId} onClose={() => setDetailId(null)} onChanged={load} />
    </div>
  );
}
